import type { VNode } from 'preact';
import type { ComponentUISpec } from '../types';
import type { JsonValue, JsonObject } from '@zenmako/contracts/common';
import { stringToColor } from '../utils/colors';
import { marked } from 'marked';

interface ToolResultRendererProps {
  uiSpec: ComponentUISpec;
  data: JsonValue;
  className?: string;
}

/**
 * Interpolate template strings like {{property}} with actual data values
 */
function interpolateTemplate(template: string, data: JsonValue): string {
  if (!template || typeof template !== 'string') return template;

  return template.replace(/\{\{([^}]+)\}\}/g, (match: string, property: string) => {
    const keys = property.trim().split('.');
    let value: JsonValue = data;

    for (const key of keys) {
      if (value && typeof value === 'object' && !Array.isArray(value) && key in value) {
        value = (value)[key];
      } else {
        return match; // Return original if property not found
      }
    }

    return value != null ? String(value) : '';
  });
}

/**
 * Convert UTF-8 byte string to proper Unicode string
 */
function utf8BytesToString(bytes: string): string {
  try {
    // Convert bytes to Uint8Array
    const uint8Array = new Uint8Array(bytes.length);
    for (let i = 0; i < bytes.length; i++) {
      uint8Array[i] = bytes.charCodeAt(i);
    }
    
    // Use TextDecoder to properly decode UTF-8
    const decoder = new TextDecoder('utf-8');
    return decoder.decode(uint8Array);
  } catch (error) {
    // Fallback to original if decoding fails
    console.warn('Failed to decode UTF-8 bytes:', error);
    return bytes;
  }
}

/**
 * Decode MIME-encoded header fields (RFC 2047)
 */
function decodeMimeHeader(text: string): string {
  if (!text) return text;

  // Handle =?charset?encoding?content?= format
  return text.replace(/=\?([^?]+)\?([BQbq])\?([^?]+)\?=/g, (match: string, _charset: string, encoding: string, content: string) => {
    try {
      if (encoding.toLowerCase() === 'b') {
        // Base64 encoding
        const decoded = atob(content);
        // Convert UTF-8 bytes to proper Unicode string
        return utf8BytesToString(decoded);
      } else if (encoding.toLowerCase() === 'q') {
        // Quoted-printable encoding
        const decodedBytes = content
          .replace(/=([A-F0-9]{2})/g, (_match: string, hex: string) => String.fromCharCode(parseInt(hex, 16)))
          .replace(/_/g, ' ');
        return utf8BytesToString(decodedBytes);
      }
    } catch (error) {
      console.warn('Failed to decode MIME header:', match, error);
    }
    return match; // Return original if decoding fails
  });
}

/**
 * Format content based on propType
 */
function formatContent(content: string, propType?: string, _data?: JsonValue): string {
  if (!propType) return content;
  
  switch (propType) {
    case 'email-text': {
      // Clean up email formatting, remove quotes and brackets, decode MIME
      const cleanText = content.replace(/^["'](.+)["']$/, '$1').replace(/^(.+)\s*<.*>$/, '$1');
      return decodeMimeHeader(cleanText);
    }

    case 'email-subject':
      // Decode MIME-encoded email subjects
      return decodeMimeHeader(content);

    case 'email-body':
      // Email body is now clean plain text from IMAP - no parsing needed
      // Convert escaped newlines to actual newlines for proper display
      return content.replace(/\\n/g, '\n');

    case 'email-date':
      // Format email date to more readable format
      try {
        const date = new Date(content);
        return date.toLocaleDateString(undefined, {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit'
        });
      } catch {
        return content;
      }

    case 'email-favicon': {
      // Extract domain from email for favicon (simplified)
      const emailMatch = content.match(/<([^>]+)>/);
      const email = emailMatch ? emailMatch[1] : content;
      const domain = email.split('@')[1];
      return domain ? `https://www.google.com/s2/favicons?domain=${domain}&sz=32` : '';
    }

    case 'date':
      // Format date to relative time or short format
      try {
        const date = new Date(content);
        return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
      } catch {
        return content;
      }
    
    default:
      return content;
  }
}

/**
 * Evaluate simple conditions like {{flags.length === 0}}
 */
function evaluateCondition(condition: string, data: JsonValue): boolean {
  if (!condition || !data || typeof data !== 'object') return false;

  // Simple condition evaluation for {{flags.length === 0}}
  const match = condition.match(/\{\{([^}]+)\}\}/);
  if (!match) return false;

  const expr = match[1].trim();

  // Handle property.length === 0
  const lengthMatch = expr.match(/^(\w+)\.length\s*===\s*(\d+)$/);
  if (lengthMatch) {
    const property = lengthMatch[1];
    const expectedLength = parseInt(lengthMatch[2], 10);
    const value = (data as JsonObject)[property];
    return Array.isArray(value) && value.length === expectedLength;
  }

  // Handle simple property existence
  const propMatch = expr.match(/^(\w+)$/);
  if (propMatch) {
    const property = propMatch[1];
    return Boolean((data as JsonObject)[property]);
  }

  return false;
}

interface SpecWithIterate extends ComponentUISpec {
  iterate?: string;
}

interface ConditionObject {
  field: string;
  operator: string;
}

/**
 * Render a single UI spec component
 */
function renderComponent(spec: ComponentUISpec | null | undefined, data: JsonValue, key?: string | number): VNode<Record<string, never>> | null {
  if (!spec) return null;

  const specWithIterate = spec as SpecWithIterate;
  const { type, class: className, content, src, alt, propType, children, condition, then, itemTemplate } = spec;
  const iterate = specWithIterate.iterate;

  // Handle conditional rendering based on condition object
  if (condition && typeof condition === 'object') {
    const condObj = condition as ConditionObject;
    const { field, operator } = condObj;
    if (operator === 'exists') {
      const keys = field.split('.');
      let value: JsonValue = data;
      for (const k of keys) {
        if (value && typeof value === 'object' && !Array.isArray(value)) {
          value = (value)[k];
        } else {
          value = null;
          break;
        }
      }
      if (!value) return null;
    }
  }

  // Handle conditional rendering (legacy)
  if (type === 'conditional') {
    if (condition && !evaluateCondition(condition, data)) {
      return null;
    }
    return then ? renderComponent(then, data, key) : null;
  }

  // Handle iteration (iterate property on any component)
  if (iterate && children) {
    const listData = (typeof data === 'object' && data !== null && !Array.isArray(data)) ? (data)[iterate] : null;

    console.log('Iterate rendering debug:', {
      iterate,
      dataKeys: (data && typeof data === 'object' && !Array.isArray(data)) ? Object.keys(data) : 'no data',
      isArray: Array.isArray(listData),
      arrayLength: Array.isArray(listData) ? listData.length : 'not array',
      listData
    });

    if (!Array.isArray(listData)) {
      console.warn(`Iterate property '${iterate}' is not an array in data`);
      return null;
    }

    return (
      <>
        {listData.map((item: JsonValue, index: number) =>
          <div key={index} className={className}>
            {children.map((child: ComponentUISpec, childIndex: number) =>
              renderComponent(child, item, childIndex)
            )}
          </div>
        )}
      </>
    );
  }

  // Handle list rendering (legacy)
  if (type === 'list' && spec.data && itemTemplate) {
    // Extract property name from template like "{{emails}}"
    const propertyMatch = spec.data.match(/^\{\{([^}]+)\}\}$/);
    if (!propertyMatch) return null;

    const propertyName = propertyMatch[1];
    const listData = (typeof data === 'object' && data !== null && !Array.isArray(data)) ? (data)[propertyName] : null;

    console.log('List rendering debug:', {
      specData: spec.data,
      propertyName,
      dataKeys: (data && typeof data === 'object' && !Array.isArray(data)) ? Object.keys(data) : 'no data',
      isArray: Array.isArray(listData),
      arrayLength: Array.isArray(listData) ? listData.length : 'not array'
    });

    if (!Array.isArray(listData)) return null;

    return (
      <>
        {listData.map((item: JsonValue, index: number) =>
          renderComponent(itemTemplate, item, index)
        )}
      </>
    );
  }
  
  // Handle basic components
  const commonProps = {
    className
  };

  // Interpolate content
  const interpolatedContent = content ? interpolateTemplate(content, data) : '';
  const formattedContent = formatContent(interpolatedContent, propType, data);

  // Render children
  const renderedChildren = children?.map((child: ComponentUISpec, index: number) =>
    renderComponent(child, data, index)
  );
  
  switch (type) {
    case 'div':
      return <div key={key} {...commonProps}>{renderedChildren}</div>;

    case 'span':
      return <span key={key} {...commonProps}>{renderedChildren || formattedContent}</span>;

    case 'card':
      return <div key={key} {...commonProps}>{renderedChildren}</div>;

    case 'card-body':
      return <div key={key} {...commonProps} style={{ padding: 0 }}>{renderedChildren}</div>;

    case 'text':
      return <div key={key} {...commonProps}>{formattedContent}</div>;

    case 'pre':
      return <pre key={key} {...commonProps}>{formattedContent}</pre>;

    case 'markdown': {
      const html = marked.parse(formattedContent, { async: false }) as string;
      return <div key={key} {...commonProps} dangerouslySetInnerHTML={{ __html: html }} />;
    }

    case 'img': {
      const interpolatedSrc = src ? interpolateTemplate(src, data) : '';
      const formattedSrc = formatContent(interpolatedSrc, propType, data);
      const interpolatedAlt = alt ? interpolateTemplate(alt, data) : '';

      return (
        <img
          key={key}
          {...commonProps}
          src={formattedSrc}
          alt={interpolatedAlt}
          onError={(e): void => {
            // Fallback to DiceBear initials
            const target = e.target as HTMLImageElement;
            const colorSeed = interpolatedAlt;
            const backgroundColor = stringToColor(colorSeed);
            target.src = `https://api.dicebear.com/9.x/initials/svg?seed=${encodeURIComponent(interpolatedAlt)}&backgroundColor=${backgroundColor}`;
            target.onerror = null; // Prevent infinite loop
            target.onload = null; // Prevent infinite loop
          }}
          onLoad={(e): void => {
            // Check if we got a 16x16 fallback when we requested 32x32
            const target = e.target as HTMLImageElement;
            if (target.naturalWidth === 16 && target.naturalHeight === 16 && formattedSrc.includes('sz=32')) {
              // This is Google's default fallback, use DiceBear instead
              const colorSeed = interpolatedAlt;
              const backgroundColor = stringToColor(colorSeed);
              target.src = `https://api.dicebear.com/9.x/initials/svg?seed=${encodeURIComponent(interpolatedAlt)}&backgroundColor=${backgroundColor}`;
              target.onerror = null; // Prevent infinite loop
              target.onload = null; // Prevent infinite loop
            }
          }}
        />
      );
    }

    default:
      // Default to div for unknown types
      return <div key={key} {...commonProps}>{renderedChildren}</div>;
  }
}

/**
 * Resolve root data path from UI spec
 */
function resolveRootData(rootPath: string, data: JsonValue): JsonValue {
  if (!rootPath) return data;

  const keys = rootPath.split('.');
  let resolvedData: JsonValue = data;

  for (const key of keys) {
    if (resolvedData && typeof resolvedData === 'object' && !Array.isArray(resolvedData) && key in resolvedData) {
      resolvedData = (resolvedData)[key];
    } else {
      console.warn(`Root path '${rootPath}' not found in data, using original data`);
      return data;
    }
  }

  return resolvedData;
}

/**
 * Tool Result Renderer - renders UI spec with data
 */
export function ToolResultRenderer({ uiSpec, data, className = '' }: ToolResultRendererProps): VNode<Record<string, never>> | null {
  // Resolve root data based on UI spec root property
  if (uiSpec.root === undefined || uiSpec.root === null) {
    console.error('UI spec missing required root property', { uiSpec, data });
    return null;
  }

  const rootData = resolveRootData(uiSpec.root, data);

  console.log('ToolResultRenderer Debug:', {
    uiSpecRoot: uiSpec.root,
    originalDataKeys: (data && typeof data === 'object' && !Array.isArray(data)) ? Object.keys(data) : 'no data',
    rootDataKeys: (rootData && typeof rootData === 'object' && !Array.isArray(rootData)) ? Object.keys(rootData) : 'no root data',
    rootData: rootData
  });

  return (
    <div className={`w-full bg-transparent ${className}`}>
      {renderComponent(uiSpec, rootData)}
    </div>
  );
}